import { useRef, useEffect } from 'react';

/* ─── Grid size variants ─── */
const COLS_D = 160, ROWS_D = 90;  // desktop
const COLS_M = 72,  ROWS_M = 42;  // mobile  — ~5px cells on 375px screens

function getGrid() {
  const mobile = window.innerWidth < 768;
  const C = mobile ? COLS_M : COLS_D;
  const R = mobile ? ROWS_M : ROWS_D;
  return { COLS: C, ROWS: R, TOTAL: C * R, mobile };
}

/* ─── Char pools ─── */
const POOL_EMPTY = [' ', ' ', ' ', ' ', ' '];
const POOL_LOW   = ['·', '1', ' ', '·', '1'];
const POOL_MID   = ['3', '4', '5', '3', '4'];
const POOL_HIGH  = ['8', '9', '8', '9', '9', '8'];
const POOLS      = [POOL_EMPTY, POOL_LOW, POOL_MID, POOL_HIGH];

/* ─── Hands image — served locally (CORS-free) ─── */
const HANDS_URL = '/adam_hands.png';

/* ─── Project images — two per project, blended 50/50 (CORS-free) ─── */
const IMAGE_SOURCES = {
  stacy:      ['/images/stacy_a.jpg',     '/images/stacy_b.png'],
  pillheads:  ['/images/pillheads_a.jpg', '/images/pillheads_b.jpg'],
  palette:    ['/images/palette_a.jpg',   '/images/palette_b.jpg'],
  repillca:   ['/images/repillca_a.jpg',  '/images/repillca_b.jpg'],
  sorrow:     ['/images/sorrow_a.jpg',    '/images/sorrow_b.jpg'],
  physical:   ['/images/physical_a.jpg',  '/images/physical_b.jpg'],
  cfw_market: ['/images/cfw_a.jpg',       '/images/cfw_b.jpg'],
};

/* ─── Utils ─── */
function rnd()          { return Math.random(); }
function rndFrom(pool)  { return pool[Math.floor(rnd() * pool.length)]; }
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

function poolForD(d) {
  if (d < 0.18) return POOL_EMPTY;
  if (d < 0.42) return POOL_LOW;
  if (d < 0.68) return POOL_MID;
  return POOL_HIGH;
}
function poolIdxForD(d) {
  if (d < 0.18) return 0;
  if (d < 0.42) return 1;
  if (d < 0.68) return 2;
  return 3;
}

/* ─── S-curve contrast boost — widens dynamic range ─── */
function contrastBoost(val) {
  const v = val * 2 - 1;
  const boosted = v * (Math.abs(v) + 0.5) / 1.5;
  return (boosted + 1) / 2;
}

/* ─── Stronger S-curve for hands — separates skin tones from cream bg ─── */
function handsContrastBoost(val) {
  const v = val * 2 - 1;
  const boosted = v * (Math.abs(v) * 2 + 0.3) / 1.5;
  return Math.max(0, Math.min(1, (boosted + 1) / 2));
}

/* ═══════════════════════════════════════════════
   PROMISE-BASED COVER-CROP IMAGE SAMPLING
   cols/rows: pass the actual grid dimensions (mobile or desktop).
   viewportAR: actual window.innerWidth/innerHeight so crop matches rendered AR.
═══════════════════════════════════════════════ */
function sampleImage(src, cols, rows, viewportAR) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const off    = document.createElement('canvas');
      off.width    = cols;
      off.height   = rows;
      const ctx    = off.getContext('2d');
      const imgAR  = img.naturalWidth / img.naturalHeight;
      let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
      if (imgAR > viewportAR) {
        sw = img.naturalHeight * viewportAR;
        sx = (img.naturalWidth - sw) / 2;
      } else {
        sh = img.naturalWidth / viewportAR;
        sy = (img.naturalHeight - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cols, rows);
      const data = ctx.getImageData(0, 0, cols, rows).data;
      const map  = new Float32Array(cols * rows);
      for (let i = 0; i < cols * rows; i++) {
        map[i] = (0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2]) / 255;
      }
      resolve(map);
    };
    img.onerror = () => resolve(new Float32Array(cols * rows).fill(0.5));
    img.src = src;
  });
}

/* ═══════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════ */
export default function UnifiedField({ hoveredProject, activeProject, contentBounds }) {
  const canvasRef        = useRef(null);
  const rafRef           = useRef(null);
  const projRef          = useRef({ hovered: null, active: null });
  const brightnessRef    = useRef({});
  const handsMapRef      = useRef(null);
  const dimRef           = useRef({ W: 0, H: 0, cW: 0, cH: 0 });
  const contentBoundsRef = useRef(null);
  const gridRef          = useRef(null);  // { COLS, ROWS, TOTAL, mobile }

  /* ─── Cell data — allocated in mount effect once grid size is known ─── */
  const cs = useRef(null);

  /* ─── Init cells (idle formula) ─── */
  function initCells(map) {
    const { TOTAL } = gridRef.current;
    const now = Date.now();
    const { opacity, targetOpacity, fontSize, targetFontSize,
            speed, chars, targetChars, nextMutate, mutateInterval, poolIdx } = cs.current;
    for (let i = 0; i < TOTAL; i++) {
      const b  = map ? map[i] : 0.5;
      const d  = 1 - b;
      const op = 0.02 + d * 0.72;
      const fs = 4 + d * 22;
      opacity[i]        = op;
      targetOpacity[i]  = op;
      fontSize[i]       = fs;
      targetFontSize[i] = fs;
      speed[i]          = 0.015 + rnd() * 0.02;
      mutateInterval[i] = 1500 + rnd() * 2500;
      nextMutate[i]     = now  + rnd() * mutateInterval[i];
      poolIdx[i]        = poolIdxForD(d);
      chars[i]          = rndFrom(poolForD(d));
      targetChars[i]    = chars[i];
    }
  }

  /* ─── Update targets: idle / hover / active ─── */
  function updateTargets(mode, pid) {
    const { TOTAL, mobile } = gridRef.current;
    const { targetOpacity, targetFontSize, speed, targetChars, poolIdx } = cs.current;
    const handsMap   = handsMapRef.current;
    const projectMap = pid ? (brightnessRef.current[pid] || null) : null;
    const fb = 0.5;

    for (let i = 0; i < TOTAL; i++) {
      let d;

      if (mode === 'idle') {
        const b = handsMap ? handsMap[i] : fb;
        d = 1 - b;
        /* Mobile: softer opacity + smaller font range — less visual noise */
        targetOpacity[i]  = mobile ? (0.01 + d * 0.42) : (0.02 + d * 0.72);
        targetFontSize[i] = mobile ? (3 + d * 13)       : (4 + d * 22);
        speed[i] = 0.015 + rnd() * 0.02;

      } else if (mode === 'hover') {
        const handB = handsMap   ? handsMap[i]   : fb;
        const projB = projectMap ? projectMap[i] : fb;
        const b = handB * 0.6 + projB * 0.4;
        d = 1 - b;
        targetOpacity[i]  = mobile ? (d * 0.65) : (d * 0.95);
        targetFontSize[i] = mobile ? (3 + d * 18) : (4 + d * 28);
        speed[i] = 0.03 + rnd() * 0.04;

      } else {
        /* Active — full project image */
        const b = projectMap ? projectMap[i] : fb;
        d = 1 - b;
        targetOpacity[i]  = mobile ? (d * 0.70) : (d * 0.95);
        targetFontSize[i] = mobile ? (3 + d * 18) : (4 + d * 28);
        speed[i] = 0.06 + rnd() * 0.08;
      }

      poolIdx[i]     = poolIdxForD(d);
      targetChars[i] = rndFrom(poolForD(d));
    }
  }

  /* ─── RAF loop ─── */
  function startRAF() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';

    function animate() {
      const { COLS, ROWS, TOTAL } = gridRef.current;   // eslint-disable-line no-unused-vars
      const { W, H, cW, cH } = dimRef.current;
      const { opacity, targetOpacity, fontSize, targetFontSize, speed,
              chars, targetChars, nextMutate, mutateInterval, poolIdx } = cs.current;
      const now    = Date.now();
      const isIdle = projRef.current.hovered === null && projRef.current.active === null;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#0D0D0D';

      /* Bucket by fontSize to minimise ctx.font state changes */
      const bkts = {};
      for (let i = 0; i < TOTAL; i++) {
        if (opacity[i] < 0.004) continue;
        const k = Math.round(fontSize[i]);
        if (!bkts[k]) bkts[k] = [];
        bkts[k].push(i);
      }
      for (const sz in bkts) {
        ctx.font = `${sz}px Ndot55`;
        for (const idx of bkts[sz]) {
          ctx.globalAlpha = opacity[idx];
          const col = idx % COLS, row = Math.floor(idx / COLS);
          ctx.fillText(chars[idx], (col + 0.5) * cW, (row + 0.5) * cH);
        }
      }

      /* Update cell states */
      for (let i = 0; i < TOTAL; i++) {
        /* Suppress digits inside active content window */
        const cb = contentBoundsRef.current;
        if (cb) {
          const col   = i % COLS;
          const row   = Math.floor(i / COLS);
          const cellX = (col + 0.5) * cW;
          const cellY = (row + 0.5) * cH;
          const PAD   = 20;
          if (cellX >= cb.left - PAD && cellX <= cb.right + PAD &&
              cellY >= cb.top  - PAD && cellY <= cb.bottom + PAD) {
            targetOpacity[i] = 0.0;
            speed[i]         = 0.08;
          }
        }

        const opD = targetOpacity[i] - opacity[i];
        if (Math.abs(opD) > 0.003) opacity[i] += opD * speed[i];
        else opacity[i] = targetOpacity[i];

        const szD = targetFontSize[i] - fontSize[i];
        if (Math.abs(szD) > 0.3) fontSize[i] += szD * speed[i];
        else fontSize[i] = targetFontSize[i];

        if (chars[i] !== targetChars[i]) {
          if (Math.abs(opD) > 0.05) { if (rnd() < 0.03) chars[i] = rndFrom(POOL_MID); }
          else chars[i] = targetChars[i];
        }

        /* Idle ambient mutation */
        if (isIdle && now >= nextMutate[i]) {
          const rv = rnd();
          if (rv < 0.20) {
            chars[i] = rndFrom(POOLS[poolIdx[i]]);
          } else if (rv < 0.30) {
            targetOpacity[i] = clamp(
              targetOpacity[i] + (rnd() < 0.5 ? 0.006 : -0.006),
              0.0, 0.74
            );
          }
          nextMutate[i] = now + mutateInterval[i];
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    }
    rafRef.current = requestAnimationFrame(animate);
  }

  /* ─── Mount ─── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    /* Determine grid size once at mount */
    const grid = getGrid();
    gridRef.current = grid;
    const { COLS, ROWS, TOTAL } = grid;

    /* Allocate cell data arrays for the correct TOTAL */
    cs.current = {
      opacity:        new Float32Array(TOTAL),
      targetOpacity:  new Float32Array(TOTAL),
      fontSize:       new Float32Array(TOTAL),
      targetFontSize: new Float32Array(TOTAL),
      speed:          new Float32Array(TOTAL),
      chars:          new Array(TOTAL).fill(' '),
      targetChars:    new Array(TOTAL).fill(' '),
      nextMutate:     new Float64Array(TOTAL),
      mutateInterval: new Float32Array(TOTAL),
      poolIdx:        new Uint8Array(TOTAL),
    };

    function applySize() {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      dimRef.current = {
        W: canvas.width, H: canvas.height,
        cW: canvas.width / COLS, cH: canvas.height / ROWS,
      };
    }
    applySize();

    /* Start with flat map — transitions in when images load */
    initCells(null);

    /* Helper — load/reload all images with current viewport AR */
    function loadAllImages() {
      const ar = window.innerWidth / window.innerHeight;

      /* Hands — double contrast boost for skin tone separation */
      sampleImage(HANDS_URL, COLS, ROWS, ar).then(rawMap => {
        const boosted = new Float32Array(TOTAL);
        for (let i = 0; i < TOTAL; i++) {
          boosted[i] = handsContrastBoost(contrastBoost(rawMap[i]));
        }
        handsMapRef.current = boosted;
        const { hovered, active } = projRef.current;
        if (!hovered && !active) updateTargets('idle', null);
      });

      /* Project images — two per project, blended 50/50 + contrast boost */
      Object.entries(IMAGE_SOURCES).forEach(([id, [urlA, urlB]]) => {
        Promise.all([
          sampleImage(urlA, COLS, ROWS, ar),
          sampleImage(urlB, COLS, ROWS, ar),
        ]).then(([mapA, mapB]) => {
          const blended = new Float32Array(TOTAL);
          for (let i = 0; i < TOTAL; i++) {
            blended[i] = contrastBoost((mapA[i] + mapB[i]) / 2);
          }
          brightnessRef.current[id] = blended;
          const { hovered, active } = projRef.current;
          if (active === id) updateTargets('active', id);
          else if (hovered === id) updateTargets('hover', id);
        });
      });
    }

    loadAllImages();

    /* Debounced resize — re-samples images with updated viewport AR (keeps same grid) */
    let rTimer;
    function onResize() {
      clearTimeout(rTimer);
      rTimer = setTimeout(() => {
        applySize();
        loadAllImages();
      }, 200);
    }
    window.addEventListener('resize', onResize);

    document.fonts.ready.then(() => startRAF());

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      clearTimeout(rTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Sync contentBounds prop → ref for RAF loop ─── */
  useEffect(() => {
    contentBoundsRef.current = contentBounds;
  }, [contentBounds]);

  /* ─── React to project hover / active ─── */
  useEffect(() => {
    if (!cs.current) return;
    projRef.current = { hovered: hoveredProject, active: activeProject };
    if (activeProject) {
      updateTargets('active', activeProject);
    } else if (hoveredProject) {
      updateTargets('hover', hoveredProject);
    } else {
      updateTargets('idle', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredProject, activeProject]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="unified-field"
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        zIndex:        2,
        willChange:    'transform',
      }}
    />
  );
}
